import { FC, useEffect, useMemo, useState, useContext } from "react";

import { Translate, GetLanguageValueAsString } from "@/app/application/Translate";
import {
	SessionKpi,
	UpdateTrainingDrillSessionRequest,
} from "@/features/training/model/Training/TrainingDrillSession";
import { DecimalEditCell } from "@/shared/data-grid";
import { useDataGridSingleClickEditing } from "@/shared/hooks";
import { AlertToast } from "@/shared/notifications/AlertToast";
import AssessmentIcon from "@mui/icons-material/Assessment";
import AssignmentIcon from "@mui/icons-material/Assignment";
import CloseIcon from "@mui/icons-material/Close";
import { CancelActionButton, SaveActionButton } from "@/shared/ui";
import {
	sharedTaskDialogActionsSx,
	sharedTaskDialogContentSx,
	sharedTaskDialogHeaderSx,
	sharedTaskDialogPanelSx,
	sharedTaskDialogPaperSx,
} from "@/styles/sharedTaskDialogStyles";
import {
	Box,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	FormControlLabel,
	IconButton,
	LinearProgress,
	Paper,
	Stack,
	Switch,
	TextField,
	Typography,
	useMediaQuery,
	useTheme,
} from "@mui/material";
import { DataGrid, GridColDef } from "@mui/x-data-grid";

import {
	useMutateCompleteTrainingDrillSession,
	useMutateUpdateTrainingDrillSession,
} from "@/service/TrainingMutations";
import { useFetchDrill, useFetchDrillSession } from "@/service/TrainingQueries";
import { Language } from "@/model/Language/Language";

import { TranslationContext } from "@/app/providers";


/* ──────────────────────────────────────────────────────────────────────────── */
/* Types                                                                        */
/* ──────────────────────────────────────────────────────────────────────────── */

interface CompleteDrillSessionKpiDialogProps {
	open: boolean;
	onClose: () => void;
	sessionId: number;
	initialKpiParameters: SessionKpi[];
}

/* ──────────────────────────────────────────────────────────────────────────── */
/* Section header                                                               */
/* ──────────────────────────────────────────────────────────────────────────── */

const SectionHeader: FC<{
	title: React.ReactNode;
	subtitle?: React.ReactNode;
	icon?: React.ReactNode;
}> = ({ title, subtitle, icon }) => (
	<Box sx={{ mb: 1.5 }}>
		<Stack direction="row" alignItems="center" gap={1}>
			{icon}
			<Typography variant="subtitle1" fontWeight={700}>
				{title}
			</Typography>
		</Stack>
		{subtitle && (
			<Typography
				variant="caption"
				color="text.secondary"
				sx={{ display: "block", mt: 0.25 }}
			>
				{subtitle}
			</Typography>
		)}
	</Box>
);

/* ──────────────────────────────────────────────────────────────────────────── */
/* Dialog                                                                      */
/* ──────────────────────────────────────────────────────────────────────────── */

export const CompleteDrillSessionKpiDialog: FC<
	CompleteDrillSessionKpiDialogProps
> = ({ open, onClose, sessionId, initialKpiParameters }) => {
	const translation: Language | null = useContext(TranslationContext);
	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

	const [kpiParameters, setKpiParameters] =
		useState<SessionKpi[]>(initialKpiParameters);
	const [comment, setComment] = useState<string>("");
	const [standin, setStandin] = useState<boolean>(false);
	const [saving, setSaving] = useState(false);

	const { cellModesModel, handleCellClick, handleCellModesModelChange } =
		useDataGridSingleClickEditing();

	const { data: session, isLoading: isLoadingSession } =
		useFetchDrillSession(sessionId);
	const drillId = session?.DrillId ?? 0;
	const { data: drill, isLoading: isLoadingDrill } = useFetchDrill(drillId);

	const { updateTrainingDrillSession } = useMutateUpdateTrainingDrillSession();
	const mutateComplete = useMutateCompleteTrainingDrillSession();

	const loading = isLoadingSession || isLoadingDrill;

	const closeButtonSx = {
		color: theme.palette.text.secondary,
		"&:hover": {
			color: theme.palette.text.primary,
			backgroundColor: theme.palette.action.hover,
		},
	};

	useEffect(() => {
		setKpiParameters(initialKpiParameters);
	}, [initialKpiParameters]);

	useEffect(() => {
		if (!open) return;
		if (session) {
			setComment(session.Comment ?? "");
			setStandin(session.Standin ?? false);
		}
	}, [open, session]);

	const handleRowUpdate = (updatedRow: SessionKpi) => {
		setKpiParameters((prev) =>
			prev.map((row) =>
				row.Id === updatedRow.Id ? { ...row, Value: updatedRow.Value } : row,
			),
		);
		return updatedRow;
	};

	const handleSaveAndComplete = async () => {
		if (saving) {
			return;
		}

		if (!session || !drill) return;

		try {
			setSaving(true);

			const request: UpdateTrainingDrillSessionRequest = {
				Id: session.Id,
				Date: session.Date,
				ResponsibleId: session.Responsible?.Id ?? 0,
				Information: session.Information,
				Comment: comment,
				KpiParameters: kpiParameters.map((k) => ({ Id: k.Id, Value: k.Value })),
				Standin: standin,
			};

			await updateTrainingDrillSession(request);
			await mutateComplete(sessionId);

			await AlertToast.success("Session completion saved");
			onClose();
		} catch (e) {
			console.error(e);
			await AlertToast.failed("Failed to save KPIs and complete session");
		} finally {
			setSaving(false);
		}
	};

	const columns: GridColDef<SessionKpi>[] = useMemo(
		() => [
			{
				field: "Value",
				headerName: "Value",
				type: "number",
				editable: true,
				renderEditCell: (params) => <DecimalEditCell {...params} />,
				align: "left",
				headerAlign: "left",
				width: 120,
			},
			{
				field: "Name",
				headerName: "Name",
				align: "left",
				headerAlign: "left",
				flex: 1,
			},
			{
				field: "MaxValue",
				headerName: "Max value",
				type: "number",
				align: "right",
				headerAlign: "right",
				width: 140,
			},
		],
		[],
	);

	return (
		<Dialog
			open={open}
			onClose={saving ? undefined : onClose}
			fullWidth
			maxWidth="md"
			fullScreen={isMobile}
			aria-labelledby="confirm-kpis-title"
			PaperProps={{
				sx: sharedTaskDialogPaperSx(theme, isMobile),
			}}
		>
			<DialogTitle
				id="confirm-kpis-title"
				component="div"
				sx={{
					...sharedTaskDialogHeaderSx(theme),
					position: "relative",
					px: { xs: 2, sm: 2.5 },
					pt: 2,
					pb: 1.5,
				}}
			>
				<Typography
					variant="caption"
					sx={{
						display: "block",
						fontWeight: 800,
						letterSpacing: "0.08em",
						color: "primary.main",
						mb: 0.4,
					}}
				>
					<Translate String="Auto.Features.Training.Pages.DrillTab.PanelRight.DrillSession.CompleteDrillSessionKpiDialog.L240C6.TRAINING">TRAINING</Translate>
				</Typography>
				<Typography
					variant="h6"
					component="h2"
					sx={{ fontWeight: 700, color: "text.primary", pr: 5, mb: 0.75 }}
				>
					<Translate String="Training.DrillSession.CompleteKpi.Title">
						Confirm KPIs before completing
					</Translate>
				</Typography>
				<Typography
					variant="body2"
					color="text.secondary"
					sx={{ mt: 0.5, display: "block" }}
				>
					<Translate String="Training.DrillSession.CompleteKpi.Subtitle">
						Review or adjust KPI values before completing this session.
					</Translate>
				</Typography>
				<IconButton
					aria-label={GetLanguageValueAsString(translation, "Auto.Features.Training.Pages.DrillTab.PanelRight.DrillSession.CompleteDrillSessionKpiDialog.L262C17.Close", "Close")}
					onClick={onClose}
					size="small"
					disabled={saving}
					sx={{ position: "absolute", right: 8, top: 8, ...closeButtonSx }}
				>
					<CloseIcon fontSize="small" />
				</IconButton>
			</DialogTitle>

			<DialogContent
				sx={{
					...sharedTaskDialogContentSx(theme),
					px: { xs: 2, sm: 2.5 },
					pt: 2,
					pb: 0,
					flex: 1,
					overflow: "auto",
				}}
			>
				{loading ? (
					<LinearProgress sx={{ width: "100%" }} />
				) : (
					<Stack spacing={3}>
						{(() => {
							const sectionCardSx = {
								...sharedTaskDialogPanelSx(theme),
								p: { xs: 2, md: 2.5 },
							};
							return (
								<>
						{/* 1. Session details */}
						<Paper variant="outlined" sx={sectionCardSx}>
							<SectionHeader
								title={
									<Translate String="Training.DrillSession.SessionDetails">
										Session details
									</Translate>
								}
								subtitle={
									<Translate String="Training.DrillSession.Complete.Description">
										Review or adjust values for this session before completing.
									</Translate>
								}
							/>
							<Stack spacing={2}>
								<FormControlLabel
									control={
										<Switch
											checked={standin}
											onChange={(e) => setStandin(e.target.checked)}
											name="Standin"
											color="primary"
										/>
									}
									labelPlacement="start"
									label={
										<Translate String="Training.DrillSession.Edit.Standin">
											Stand in
										</Translate>
									}
									sx={{
										m: 0,
										gap: 1,
										alignItems: "center",
										flexWrap: "nowrap",
										"& .MuiFormControlLabel-label": {
											marginRight: 0,
										},
									}}
								/>
								<TextField
									label={
										<Translate String="Training.DrillSession.Edit.Comment">
											Comment
										</Translate>
									}
									value={comment}
									onChange={(e) => setComment(e.target.value)}
									placeholder="Closing comments about this session"
									fullWidth
									multiline
									minRows={3}
									size="small"
									sx={{
										"& .MuiOutlinedInput-root": {
											borderRadius: 2,
											bgcolor: "background.paper",
										},
									}}
								/>
							</Stack>
						</Paper>

						{/* 2. KPI Parameters */}
						{kpiParameters && kpiParameters.length > 0 && (
							<Paper variant="outlined" sx={sectionCardSx}>
								<SectionHeader
									title={
										<Translate String="Training.DrillSession.Edit.KpiParameters">
											KPI Parameters
										</Translate>
									}
									subtitle={
										<Translate String="Training.DrillSession.CompleteKpi.EditHint">
											Click a value to edit.
										</Translate>
									}
								/>
								<Box sx={{ width: "100%", minHeight: 240 }}>
									<DataGrid<SessionKpi>
										rows={kpiParameters}
										columns={columns}
										getRowId={(row) => row.Id}
										processRowUpdate={handleRowUpdate}
										cellModesModel={cellModesModel}
										onCellModesModelChange={handleCellModesModelChange}
										onCellClick={handleCellClick}
										disableColumnMenu
										hideFooter
										getRowSpacing={() => ({ top: 5 })}
										sx={{
											border: "none",
											"& .MuiDataGrid-cell": {
												borderColor: "divider",
											},
											"& .MuiDataGrid-columnHeaders": {
												borderColor: "divider",
											},
										}}
									/>
								</Box>
							</Paper>
						)}
								</>
							);
						})()}
					</Stack>
				)}
			</DialogContent>

			<DialogActions
				sx={{
					...sharedTaskDialogActionsSx(theme, isMobile),
					px: 0,
					pt: 2,
					mt: 3,
					flexWrap: "wrap",
					justifyContent: "flex-end",
					gap: 1.5,
					display: "flex",
				}}
			>
				<CancelActionButton onClick={onClose} disabled={saving}>
					<Translate String="Procedure.Button.Cancel">Cancel</Translate>
				</CancelActionButton>
				<SaveActionButton
					onClick={() => {
						void handleSaveAndComplete();
					}}
					loading={saving}
					loadingText={
						<Translate String="LabDaily.Saving">Saving...</Translate>
					}
					disabled={saving || loading}
				>
					<Translate String="Training.DrillSession.CompleteKpi.SaveComplete">
						Save & complete
					</Translate>
				</SaveActionButton>
			</DialogActions>
		</Dialog>
	);
};
